import { useEffect, useMemo, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import clsx from 'clsx';
import { reportApi } from '../../services/api';
import { useReportStore, isUnackedUrgent, type FieldReport } from '../../store/reportStore';
import { usePlatoonStore } from '../../store/platoonStore';
import { beep } from '../../lib/alerts';
import { REPORT_TYPES, REPORT_PRIORITIES, REPORT_TYPE_META, PRIORITY_META } from '../../lib/reports';
import ReportCard from './ReportCard';

const SIREN_INTERVAL_MS = 12000;

function tsMillis(iso: string): number {
  const t = new Date(iso).getTime();
  return Number.isNaN(t) ? 0 : t;
}

// Repeating audible siren while any unacked FLASH/IMMEDIATE report is present
// (docs/19 §1.2). Uses the shared beep() so it matches the command-center tone.
function useReportSiren(active: boolean) {
  useEffect(() => {
    if (!active) return;
    beep('alert');
    const t = setInterval(() => beep('alert'), SIREN_INTERVAL_MS);
    return () => clearInterval(t);
  }, [active]);
}

interface Props {
  compact?: boolean;
  /** Cap rows in compact mode (Command Center widget). */
  limit?: number;
}

export default function ReportsPanel({ compact = false, limit }: Props) {
  const queryClient = useQueryClient();
  const live = useReportStore((s) => s.liveReports);
  const setLive = useReportStore((s) => s.setLive);
  const platoons = usePlatoonStore((s) => s.platoons);
  const fetchPlatoons = usePlatoonStore((s) => s.fetchPlatoons);

  const [typeFilter, setTypeFilter] = useState('');
  const [priorityFilter, setPriorityFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [platoonFilter, setPlatoonFilter] = useState('');
  const [sinceFilter, setSinceFilter] = useState('');

  useEffect(() => {
    if (platoons.length === 0) fetchPlatoons();
  }, [platoons.length, fetchPlatoons]);

  const snapshotQ = useQuery({
    queryKey: ['reports', typeFilter, priorityFilter, statusFilter, platoonFilter, sinceFilter],
    queryFn: async () => {
      const res = await reportApi.getAll({
        type: typeFilter || undefined,
        priority: priorityFilter || undefined,
        status: statusFilter || undefined,
        platoonId: platoonFilter || undefined,
        since: sinceFilter ? new Date(sinceFilter).toISOString() : undefined,
        limit: 200,
      });
      return (res.data?.data?.reports ?? []) as FieldReport[];
    },
    retry: false,
    refetchInterval: 15000,
  });

  // Seed the live store from the first snapshot so the global siren has data
  // even before any socket delta arrives.
  useEffect(() => {
    if (snapshotQ.data && live.length === 0) setLive(snapshotQ.data);
  }, [snapshotQ.data, live.length, setLive]);

  const ackMutation = useMutation({
    mutationFn: (id: string) => reportApi.ack(id),
    onSuccess: () => {
      toast.success('Report acknowledged');
      queryClient.invalidateQueries({ queryKey: ['reports'] });
    },
    onError: (e: any) => toast.error(e?.response?.data?.error || 'Failed to acknowledge'),
  });

  // Merge query snapshot with the live socket buffer (dedupe by id, newest first),
  // then apply the active filters to the union so live arrivals honor filters too.
  const reports = useMemo(() => {
    const byId = new Map<string, FieldReport>();
    [...(snapshotQ.data ?? []), ...live].forEach((r) => r?.id && byId.set(r.id, r));
    let list = Array.from(byId.values());
    if (typeFilter) list = list.filter((r) => r.type === typeFilter);
    if (priorityFilter) list = list.filter((r) => r.priority === priorityFilter);
    if (statusFilter) list = list.filter((r) => r.status === statusFilter);
    if (platoonFilter) list = list.filter((r) => r.platoonId === platoonFilter);
    if (sinceFilter) {
      const cut = new Date(sinceFilter).getTime();
      list = list.filter((r) => tsMillis(r.createdAt) >= cut);
    }
    list.sort((a, b) => tsMillis(b.createdAt) - tsMillis(a.createdAt));
    return limit ? list.slice(0, limit) : list;
  }, [snapshotQ.data, live, typeFilter, priorityFilter, statusFilter, platoonFilter, sinceFilter, limit]);

  // Siren keys off the FULL live buffer (not the filtered view) so filtering the
  // table can never silence an outstanding FLASH/IMMEDIATE.
  const hasUrgent = useMemo(() => live.some(isUnackedUrgent), [live]);
  useReportSiren(hasUrgent);

  const urgentCount = useMemo(() => live.filter(isUnackedUrgent).length, [live]);

  return (
    <div className="rounded-xl border border-command-border bg-command-panel flex flex-col">
      <div className="flex items-center gap-2 p-3 border-b border-command-border flex-wrap">
        <h3 className="text-sm font-semibold text-gray-100 mr-auto flex items-center gap-2">
          Field Reports
          {urgentCount > 0 && (
            <span className="px-2 py-0.5 rounded-full bg-severity-critical/20 text-severity-critical text-[10px] font-bold border border-severity-critical/40 animate-pulse">
              {urgentCount} UNACKED
            </span>
          )}
        </h3>
        {!compact && (
          <>
            <select aria-label="Filter by type" value={typeFilter} onChange={(e) => setTypeFilter(e.target.value)}
              className="px-2 py-1 rounded bg-command-bg border border-command-border text-xs text-gray-100">
              <option value="">All types</option>
              {REPORT_TYPES.map((t) => <option key={t} value={t}>{REPORT_TYPE_META[t].label}</option>)}
            </select>
            <select aria-label="Filter by priority" value={priorityFilter} onChange={(e) => setPriorityFilter(e.target.value)}
              className="px-2 py-1 rounded bg-command-bg border border-command-border text-xs text-gray-100">
              <option value="">All priorities</option>
              {REPORT_PRIORITIES.map((p) => <option key={p} value={p}>{PRIORITY_META[p].label}</option>)}
            </select>
            <select aria-label="Filter by status" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}
              className="px-2 py-1 rounded bg-command-bg border border-command-border text-xs text-gray-100">
              <option value="">All statuses</option>
              <option value="RECEIVED">New</option>
              <option value="ACKED">Acknowledged</option>
            </select>
            <select aria-label="Filter by platoon" value={platoonFilter} onChange={(e) => setPlatoonFilter(e.target.value)}
              className="px-2 py-1 rounded bg-command-bg border border-command-border text-xs text-gray-100">
              <option value="">All platoons</option>
              {platoons.map((p) => <option key={p.id} value={p.id}>{p.callsign}</option>)}
            </select>
            <input type="datetime-local" aria-label="Since" value={sinceFilter} onChange={(e) => setSinceFilter(e.target.value)}
              className="px-2 py-1 rounded bg-command-bg border border-command-border text-xs text-gray-100" />
          </>
        )}
      </div>

      <div className={clsx('overflow-auto p-3 space-y-2', compact ? 'max-h-80' : 'max-h-[72vh]')}>
        {reports.map((r) => (
          <ReportCard key={r.id} report={r} onAck={(id) => ackMutation.mutate(id)}
            acking={ackMutation.isPending && ackMutation.variables === r.id} compact={compact} />
        ))}
        {reports.length === 0 && (
          <div className="py-10 text-center text-gray-500 text-sm">
            {snapshotQ.isError ? 'Reports feed unavailable.' : 'No reports match the current filters.'}
          </div>
        )}
      </div>
    </div>
  );
}
